import AccountContext from 'context/AccountContext';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ParkingSession, PredefinedTicket } from 'types/generatedSchemaTypes';
import { getPermitsForVehicle, getPermitTitle } from 'utils/permitUtils';
import useOngoingChargingSession from './useOngoingChargingSession';
import useOngoingParkingSession from './useOngoingParkingSession';
import useReadPurchasedPrepaidTickets from './useReadPurchasedPrepaidTickets';

export interface Purchase {
  id: string;
  name: string;
  type: string;
}

const usePurchasesForVehicle = (licensePlate: string) => {
  const { t } = useTranslation();
  const { ongoingChargingSession } = useOngoingChargingSession();
  const { allParkingSessions } = useOngoingParkingSession();
  const {
    customer: { permits },
  } = useContext(AccountContext);
  const { purchasedPrepaidTickets } = useReadPurchasedPrepaidTickets({
    fetchAll: true,
  });

  const prepaidsForVehicle = useMemo(
    () =>
      purchasedPrepaidTickets?.filter(
        (ticket) => ticket?.accessDevice?.value === licensePlate
      ),
    [licensePlate, purchasedPrepaidTickets]
  );
  const sessionsForVehice = useMemo(
    () =>
      allParkingSessions?.filter(
        (session) => session?.accessDevice?.value === licensePlate
      ),
    [licensePlate, allParkingSessions]
  );

  const combinedSessions = useMemo(
    () => [...(prepaidsForVehicle || []), ...(sessionsForVehice || [])],
    [prepaidsForVehicle, sessionsForVehice]
  );

  const permitsForVehicle = useMemo(
    () => getPermitsForVehicle(permits, licensePlate),
    [permits, licensePlate]
  );

  const permitsToPurchase = permitsForVehicle?.map((permit) => {
    const { id, permitType, orderLineIdWithVersion } = permit;
    return {
      id: permitType === 'SALES_ORDER' ? orderLineIdWithVersion : id,
      name: getPermitTitle(permit),
      type: permitType,
    };
  });

  const sessionsToPurchase = combinedSessions.map(
    (session: PredefinedTicket | ParkingSession | null | undefined) => {
      if (session?.__typename === 'ParkingSession') {
        const parkingSession = session as ParkingSession;
        return {
          id: parkingSession.sessionId,
          type: parkingSession.type,
          name: t('vehicle.removeVehicleSessionType.PARKING'),
        };
      }
      const prepaidTicket = session as PredefinedTicket;
      return {
        id: prepaidTicket.sessionId,
        type: prepaidTicket.type,
        name: prepaidTicket.parkZoneName,
      };
    }
  );

  const chargingToPurchase =
    ongoingChargingSession?.licensePlate === licensePlate
      ? [
          {
            id: ongoingChargingSession?.chargingSessionId,
            type: 'CHARGING',
            name: t('vehicle.removeVehicleSessionType.CHARGING'),
          },
        ]
      : [];

  const blockVehicleDelete =
    permitsForVehicle?.length > 0 ||
    prepaidsForVehicle?.length > 0 ||
    (sessionsForVehice?.length ?? 0) > 0 ||
    ongoingChargingSession?.licensePlate === licensePlate;

  const memoizedValues = useMemo(
    () => ({
      blockVehicleDelete: blockVehicleDelete,
      purchases: [
        ...permitsToPurchase,
        ...sessionsToPurchase,
        ...chargingToPurchase,
      ] as Purchase[],
    }),
    [
      blockVehicleDelete,
      permitsToPurchase,
      sessionsToPurchase,
      chargingToPurchase,
    ]
  );

  return memoizedValues;
};

export default usePurchasesForVehicle;
